import React, { useEffect, useState } from "react";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import {
  ADD_LABEL,
  COLLABORATOR_COMMENT_LABEL,
  EMPTY_COLLABORATOR_COMMENT_MESSAGE,
  EMPTY_STRING,
  LEVEL_OF_SATISFACTION_LABEL,
} from "../../../../../constants/Dictionary";
import {
  StyledBodyCollaboratorComment,
  StyledFixedHeight,
  StyledFormFieldWrapper,
} from "./CollaboratorComment.styled";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import { FormField } from "../../../../../components/core/FormField/FormField";
import TextArea from "../../../../../components/core/TextArea/TextArea";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { InfoContainer } from "../../../../../components/core/InfoContainer/InfoContainer";
import { StyledCollaboratorFeedbackBodyStar } from "../../Resume/Resume.styled";
import { StarFeedback } from "../../../../../components/StarFeedback/StarFeedback";
import { BodyTyp } from "../../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../../types/AppTypes";
import { TextAreaBox } from "../../../../../components/TextAreaBox/TextAreaBox";
import commentImage from "../../../../../images/comment.png";
import { ReactComponent as AddIcon } from "../../../../../icons/add.svg";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import { CmSheetSectionProps } from "../SchedaCM";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import { CHARACTER_MAX_LENGTH } from "../../../../../constants/AppConstants";
import {
  useCmSheetCollaboratorFeedbacks,
  useUpdateCmSheetCollaboratorFeedbacks,
} from "../../../../../hooks/cmSheet/collaboratorFeedback.hook";

type FormData = {
  levelSatisfaction?: number | null;
  collaboratorComment?: string;
};

export const CollaboratorComment = ({
  footer,
  cmSheetInfo,
  isMyProfile,
  cmSheetStateMachineInfo,
  showFooter,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);
  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  )?.config;

  const { data, isFetching } = useCmSheetCollaboratorFeedbacks(cmSheetInfo.id);
  const { updateCollaboratorFeedbacks, isUpdating } =
    useUpdateCmSheetCollaboratorFeedbacks(cmSheetInfo.id);

  const [hasData, setHasData] = useState<boolean | null>(null);

  const optionSelectLevelSatisfaction = selectedYearConfig?.feedbacksEvaluation
    ? selectedYearConfig.feedbacksEvaluation.map((item) => ({
        value: item.id,
        label: item.description,
      }))
    : [];

  const schema = yup
    .object({
      levelSatisfaction: yup.number().nullable(),
      collaboratorComment: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const body = {
        satisfactionLevelId: data.levelSatisfaction ?? null,
        collaboratorComment: data.collaboratorComment ?? "",
      };

      await updateCollaboratorFeedbacks(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      reset({
        levelSatisfaction: data?.satisfactionLevelId ?? null,
        collaboratorComment: data?.collaboratorComment ?? "",
      });

      if (data && hasData === null) {
        const newHasData =
          data.satisfactionLevelId !== null || !!data.collaboratorComment;
        setHasData(newHasData);
      }
    }
  }, [data, selectedYearConfig, hasData, edit, isFetching]);

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  const showLoader = isFetching && hasData === null;
  const showEmptyState = !isFetching && !hasData && !edit;
  const showPage = hasData === true || edit;

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="collaboratorComment"
        disabledEditButton={!isDirty || isUpdating}
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        setEdit={setEdit}
        showFooter={showFooter}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    isUpdating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  const starToFillCollaboratorComment =
    selectedYearConfig?.feedbacksEvaluation.find(
      (item) => item.id === data?.satisfactionLevelId,
    )?.stars;

  const descriptionCollaboratorComment =
    selectedYearConfig?.feedbacksEvaluation.find(
      (item) => item.id === data?.satisfactionLevelId,
    )?.description;

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_COLLABORATOR_COMMENT_MESSAGE : ""}
            imageSrc={commentImage}
          >
            {canEdit() && (
              <Button
                label={ADD_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<AddIcon />}
                onClick={() => (setEdit ? setEdit(true) : {})}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <StyledBodyCollaboratorComment $editMode={edit}>
          <Loader loading={isUpdating}>
            <StyledHideOrShowContainer $show={edit}>
              <form id="collaboratorComment" onSubmit={handleSubmit(onSubmit)}>
                <StyledFormFieldWrapper $editMode={edit}>
                  <FormField error={errors.levelSatisfaction?.message}>
                    <Controller
                      name="levelSatisfaction"
                      control={control}
                      render={({ field }) => (
                        <Select
                          variant={SelectVariantEnum.SINGLE_SELECT}
                          label={LEVEL_OF_SATISFACTION_LABEL}
                          options={optionSelectLevelSatisfaction}
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                  <FormField error={errors.collaboratorComment?.message}>
                    <Controller
                      name="collaboratorComment"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label={COLLABORATOR_COMMENT_LABEL}
                          maxLength={CHARACTER_MAX_LENGTH}
                          {...field}
                        />
                      )}
                    />
                  </FormField>
                </StyledFormFieldWrapper>
              </form>
            </StyledHideOrShowContainer>
            <StyledHideOrShowContainer $show={!edit}>
              <StyledFormFieldWrapper $editMode={edit}>
                <InfoContainer variant="2">
                  <StyledCollaboratorFeedbackBodyStar>
                    {data?.satisfactionLevelId ? (
                      <>
                        <StarFeedback
                          starToFill={starToFillCollaboratorComment ?? 0}
                          starNumber={
                            selectedYearConfig?.feedbacksEvaluation?.length ?? 0
                          }
                        />
                        <BodyTyp
                          variant="2"
                          fontWeight={FontWeightEnum.SEMIBOLD}
                        >
                          {descriptionCollaboratorComment}
                        </BodyTyp>
                      </>
                    ) : (
                      EMPTY_STRING
                    )}
                  </StyledCollaboratorFeedbackBodyStar>
                </InfoContainer>
                <InfoContainer variant="2">
                  <StyledFixedHeight $isEmpty={!data?.collaboratorComment}>
                    <div className="infoBox">
                      <TextAreaBox
                        text={data?.collaboratorComment}
                        variant="2"
                        fontWeight={FontWeightEnum.MEDIUM}
                      />
                    </div>
                  </StyledFixedHeight>
                </InfoContainer>
              </StyledFormFieldWrapper>
            </StyledHideOrShowContainer>
          </Loader>
        </StyledBodyCollaboratorComment>
      </StyledHideOrShowContainer>
    </>
  );
};
